import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { format, parseISO } from 'date-fns';
import { ShellTrackHandlers } from '@cigna/chcp/shared/analytics-util';
import { UserAlert } from '@cigna/chcp/shared/util';

const ALERTS_USER_STORY = 'PC9TP-5353';

const ALERT_TYPE_LABELS: Record<string, string> = {
  CONTRACT_TERMINATION: 'Immediate action required',
  SUPPRESSION: 'Action required',
  DIRECTORY_UPDATE_REQUIRED: 'Review required',
};

const ALERT_CLICK_LABELS: Record<string, string> = {
  CONTRACT_TERMINATION: 'Termination - Immediate action required',
  SUPPRESSION: 'Removal - Action required',
  DIRECTORY_UPDATE_REQUIRED: 'Directory Update - Review required',
};

@Component({
  selector: 'cigna-alerts-tasks',
  templateUrl: './alerts-tasks.component.html',
  styleUrl: './alerts-tasks.component.scss',
})
export class AlertsTasksComponent implements OnChanges {
  constructor(
    private router: Router,
    private analytics: ShellTrackHandlers,
    private translate: TranslateService,
  ) {}

  @Input() alerts: UserAlert[] = [];

  @Input() isImpersonator = false;

  @Output() startReview = new EventEmitter<UserAlert>();

  isExpanded = false;

  private trackedAlertIds = '';

  get hasMultipleAlerts(): boolean {
    return this.alerts.length >= 2;
  }

  get visibleAlerts(): UserAlert[] {
    return !this.hasMultipleAlerts || this.isExpanded ? this.alerts : [];
  }

  ngOnChanges(): void {
    this.trackAlertsDisplayed();
  }

  toggleAlerts(): void {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) {
      this.analytics.trackEventOther(
        {},
        'alert accordion opened',
        'ac-DB_GB003',
        {
          eVar120: 'alert-accordion-click',
          eVar119: ALERTS_USER_STORY,
        },
      );
    }
  }

  onStartReview(alert: UserAlert): void {
    if (this.isImpersonator) {
      return;
    }
    this.analytics.trackEventOther(
      {},
      this.translate.instant(this.getCopyKey(alert, 'ctaText')),
      'ac-DB_GB002',
      {
        eVar120: 'alert-click',
        eVar119: ALERTS_USER_STORY,
        prop20: ALERT_CLICK_LABELS[alert.alertType] ?? alert.alertType,
      },
    );
    this.startReview.emit(alert);
    this.isExpanded = false;
    this.router.navigateByUrl(`/my-practice/directory/update/${alert.tin}`);
  }

  getStatus(alert: UserAlert): 'error' | 'warning' {
    return alert.priority === 1 ? 'error' : 'warning';
  }

  getEmphasis(alert: UserAlert): 'strong' | 'subtle' {
    return alert.priority === 1 ? 'strong' : 'subtle';
  }

  getCopyKey(alert: UserAlert, entry: 'heading' | 'body' | 'ctaText'): string {
    return `shared.alertsTasks.${alert.alertType}.${entry}`;
  }

  getCopyParams(alert: UserAlert): Record<string, string | undefined> {
    return {
      tin: alert.tin,
      deadlineDate: alert.expirationDate
        ? format(parseISO(alert.expirationDate), 'MM/dd/yyyy')
        : alert.expirationDate,
    };
  }

  private trackAlertsDisplayed(): void {
    const alerts = this.alerts;
    const alertIds = alerts.map((alert) => alert.alertId).join(',');
    if (!alerts.length || alertIds === this.trackedAlertIds) {
      return;
    }
    this.trackedAlertIds = alertIds;
    this.analytics.trackEventOther({}, 'Alert displayed', 'ac-DB_GB001', {
      eVar120: 'alert-displayed',
      eVar119: ALERTS_USER_STORY,
      eVar111: alerts
        .map(
          (alert) =>
            `Alert Type:${
              ALERT_TYPE_LABELS[alert.alertType] ?? alert.alertType
            }`,
        )
        .join('^'),
    });
  }
}
